import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../services/api";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  FolderOpen,
  ShoppingBag,
  Menu,
  X,
  Home,
  Users,
  Info,
  Contact
} from "lucide-react";

import logo from "../assets/logo.jpeg";

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await api.post("/logout");
  
      sessionStorage.removeItem("user"); // clearer than clear("user")
      localStorage.removeItem("user");   // if used
  
      navigate("/", { replace: true });
    } catch (err) {
      console.error("Logout failed");
    }
  };

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
  
    window.onpopstate = () => {
      window.history.pushState(null, "", window.location.href);
    };
  
    return () => {
      window.onpopstate = null; // cleanup when leaving admin
    };
  }, []);

  const navItems = [
    { name: "Dashboard", path: "/admin", icon: LayoutDashboard },
    { name: "Products", path: "/admin/products", icon: Package },
    { name: "Categories", path: "/admin/categories", icon: FolderOpen },
    { name: "Orders", path: "/admin/orders", icon: ShoppingBag },
    { name: "Users", path: "/admin/users", icon: Users },
    { name: "About us", path: "/admin/about", icon: Info },
     { name: "Contact us", path: "/admin/contact", icon: Contact },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-sidebar text-sidebar-foreground transition-all duration-300 ${
          sidebarOpen ? "w-64" : "w-0 md:w-20"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div
            className={`p-6 flex items-center ${
              sidebarOpen ? "gap-3" : "justify-center"
            }`}
          >
            <img
              src={logo}
              alt="Herbatica"
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
            />
            {sidebarOpen && (
              <span className="text-xl font-serif font-bold">Herbatica</span>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`admin-nav-item ${
                  isActive(item.path) ? "active" : ""
                } ${sidebarOpen ? "" : "justify-center"}`}
              >
                <item.icon size={20} />
                {sidebarOpen && <span>{item.name}</span>}
              </Link>
            ))}
          </nav>

          {/* Back to Site */}
          <div className="p-4 border-t border-sidebar-border">
            <button
              onClick={handleLogout}
              className={`admin-nav-item text-sidebar-foreground/70 hover:text-sidebar-foreground ${
                sidebarOpen ? "" : "justify-center"
              }`}
            >
              <Home size={20} />
              {sidebarOpen && <span>Back to Site</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`flex-1 transition-all duration-300 ${
          sidebarOpen ? "md:ml-64" : "md:ml-20"
        }`}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-40 bg-card border-b border-border px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <h1 className="text-xl font-serif font-semibold">
              Admin Dashboard
            </h1>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">{children}</main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-foreground/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
